import { useEffect, useState } from "react";
import { CircleCheck, CircleHelp, Download, Send } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { EvaluationRequestForm } from "@/components/StaffEvaluationRequestForm";
import { api, formatApiError } from "@/lib/api";

export default function ClientEvaluationRequestPage() {
  const [requests, setRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [responses, setResponses] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [formKey, setFormKey] = useState(0);

  async function loadRequests() {
    try {
      const response = await api.get("/client-evaluation-requests/mine");
      setRequests(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
      setNotice("");
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    loadRequests();
  }, []);

  async function respond(requestId) {
    try {
      await api.post(`/client-evaluation-requests/${requestId}/respond`, {
        response: responses[requestId] || "",
      });
      setResponses({ ...responses, [requestId]: "" });
      setNotice("TAG received your additional information.");
      await loadRequests();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function downloadReport(item) {
    try {
      const response = await api.get(`/client-evaluation-requests/${item.id}/report/client-download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `evaluation-report-${item.id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <div className="app-shell" data-testid="client-evaluation-request-shell">
      <AppSidebar activeItem="Evaluation Requests" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page client-evaluation-page" data-testid="client-evaluation-request-page">
          <div className="page-intro">
            <div>
              <p className="eyebrow">CLIENT PORTAL</p>
              <h1>Evaluation Request</h1>
              <p className="page-subtitle">Submit the information TAG needs to coordinate a student evaluation.</p>
            </div>
          </div>

          {error && (
            <p className="form-message error-message" data-testid="client-evaluation-error">
              {error}
            </p>
          )}
          {notice && (
            <p className="form-message success-message" data-testid="client-evaluation-success">
              {notice}
            </p>
          )}

          <EvaluationRequestForm
            key={formKey}
            mode="client"
            onCreated={(created) => {
              setFormKey((current) => current + 1);
              setNotice(
                created.consent_document
                  ? "Evaluation request and signed consent submitted to TAG for review and provider assignment."
                  : "Evaluation request submitted to TAG for review. Upload the signed consent when it is ready.",
              );
              setError("");
              loadRequests();
            }}
          />

          <section className="client-evaluation-status-panel" data-testid="client-evaluation-status-panel">
            <div className="evaluation-section-heading">
              <CircleCheck size={18} />
              <div>
                <h2>My evaluation requests</h2>
                <p>Track TAG’s review status, provider assignment, and due dates.</p>
              </div>
            </div>
            <div className="client-evaluation-status-list">
              {requests.map((item) => (
                <article data-testid={`client-evaluation-status-${item.id}`} key={item.id}>
                  <div>
                    <strong>{item.status}</strong>
                    <span data-testid={`client-evaluation-summary-${item.id}`}>{[item.student_initials, item.evaluation_type].filter(Boolean).join(" · ")}</span>
                    <span>{item.provider_name || "Provider not assigned"}</span>
                  </div>
                  <div>
                    <small>{item.due_date ? `Target due ${item.due_date}` : "Due date pending"}</small>
                    {item.client_message && <p>{item.client_message}</p>}
                    {item.report_available && (
                      <button className="button button-secondary" data-testid={`download-client-evaluation-report-${item.id}`} type="button" onClick={() => downloadReport(item)}>
                        <Download size={15} /> Download evaluation report
                      </button>
                    )}
                  </div>
                  {item.awaiting_client_information && (
                    <div className="client-evaluation-response-form">
                      <CircleHelp size={16} />
                      <textarea
                        data-testid={`client-evaluation-response-${item.id}`}
                        value={responses[item.id] || ""}
                        onChange={(event) => setResponses({ ...responses, [item.id]: event.target.value })}
                        placeholder="Add the information TAG requested"
                        rows="3"
                      />
                      <button
                        className="button button-secondary"
                        data-testid={`submit-client-evaluation-response-${item.id}`}
                        disabled={!responses[item.id]?.trim()}
                        type="button"
                        onClick={() => respond(item.id)}
                      >
                        <Send size={15} /> Send information
                      </button>
                    </div>
                  )}
                </article>
              ))}
              {!loaded && !requests.length && <p className="provider-empty-state list-loading" data-testid="client-evaluation-status-loading">Loading evaluation requests…</p>}
              {loaded && !requests.length && (
                <p className="provider-empty-state" data-testid="client-evaluation-status-empty">
                  No evaluation requests have been submitted yet.
                </p>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
